import axios from 'axios';
import { API_ORIGIN } from '../config/env';
import { getAccessToken, refresh, isV2, isAuthFailure, notifySessionLost, notifyPasswordResetRequired } from '../auth/session';

// Auth Phase 1 (P1-B): the axios client never touches storage. It asks the
// session module for a token (which refreshes silently when needed), and on a
// 401 that means "bad token" it refreshes once and retries once. A refresh
// that itself fails ends the session (the session module emits 'lost' and the
// app shows the sign-in modal) — there is NO hard navigation here any more.
const api = axios.create({
  baseURL: `${API_ORIGIN}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    // P2-A (R6): a forced-reset token used outside its allowlist → the app
    // shows the "Set a new password" view. The request still rejects.
    if (response?.status === 403 && response.data?.code === 'PASSWORD_RESET_REQUIRED') {
      notifyPasswordResetRequired();
      return Promise.reject(error);
    }
    if (response?.status !== 401 || !config || !isAuthFailure(response.data)) {
      return Promise.reject(error);
    }
    if (isV2() && !config._retried) {
      config._retried = true;
      try {
        await refresh(); // a 401 here emits 'lost' inside the session module
      } catch (e) {
        return Promise.reject(error);
      }
      const token = await getAccessToken();
      config.headers.Authorization = `Bearer ${token}`;
      return api(config);
    }
    if (!isV2()) {
      // v1 fallback: an expired 7-day token has no refresh path — session lost.
      notifySessionLost({ code: 'V1_TOKEN_INVALID', message: response.data?.message });
    }
    return Promise.reject(error);
  }
);

export default api;
